import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { StaffGuard, ClientGuard, GuestGate, RootGate, ContractorGuard } from "@/components/Guards";
import { Toaster } from "@/components/ui/sonner";

import AppLayout from "@/components/AppLayout";
import PortalLayout from "@/components/PortalLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PortalLogin from "@/pages/PortalLogin";
import Dashboard from "@/pages/Dashboard";
import Buildings from "@/pages/Buildings";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import BuildingDetail from "@/pages/BuildingDetail";
import FloorPlans from "@/pages/FloorPlans";
import DoorDetail from "@/pages/DoorDetail";
import Inspections from "@/pages/Inspections";
import InspectionDetail from "@/pages/InspectionDetail";
import InspectionForm from "@/pages/InspectionForm";
import Quotes from "@/pages/Quotes";
import QuoteCreate from "@/pages/QuoteCreate";
import QuoteDetail from "@/pages/QuoteDetail";
import QuoteFullEdit from "@/pages/QuoteFullEdit";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Maintenance from "@/pages/Maintenance";
import MaintenanceDetail from "@/pages/MaintenanceDetail";
import Schedule from "@/pages/Schedule";
import ExternalReports from "@/pages/ExternalReports";
import ExternalReportDetail from "@/pages/ExternalReportDetail";
import Reports from "@/pages/Reports";
import ReportBuilder from "@/pages/ReportBuilder";
import InvoiceCreate from "@/pages/InvoiceCreate";
import InspectionRequests from "@/pages/InspectionRequests";
import PortalBuildings from "@/pages/portal/PortalBuildings";
import PortalBuildingDetail from "@/pages/portal/PortalBuildingDetail";
import PortalRequestInspection from "@/pages/portal/PortalRequestInspection";
import RemedialSchedule from "@/pages/RemedialSchedule";
import Reinspections from "@/pages/Reinspections";
import LabelSheet from "@/pages/LabelSheet";
import PortalReportFailure from "@/pages/portal/PortalReportFailure";
import PortalUploadReport from "@/pages/portal/PortalUploadReport";
import PortalMaintenance from "@/pages/portal/PortalMaintenance";
import PortalAccount from "@/pages/portal/PortalAccount";
import PortalRegister from "@/pages/portal/PortalRegister";
import Settings from "@/pages/settings/Settings";
import Platform from "@/pages/platform/Platform";
import PortalHome from "@/pages/portal/PortalHome";
import PortalQuotes from "@/pages/portal/PortalQuotes";
import PortalQuoteDetail from "@/pages/portal/PortalQuoteDetail";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalInvoiceDetail from "@/pages/portal/PortalInvoiceDetail";
import PortalTenders from "@/pages/portal/PortalTenders";
import ContractorApply from "@/pages/partner/ContractorApply";
import ContractorPortal from "@/pages/partner/ContractorPortal";
import ModeSelect from "@/pages/ModeSelect";
import EngineerJobs from "@/pages/EngineerJobs";
import PaymentSuccess from "@/pages/PaymentSuccess";
import LegalDoc from "@/pages/legal/LegalDoc";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<RootGate><Landing /></RootGate>} />
          <Route path="/legal/:type" element={<LegalDoc />} />
          <Route path="/login" element={<GuestGate><Login /></GuestGate>} />
          <Route path="/register" element={<GuestGate><Register /></GuestGate>} />
          <Route path="/portal/login" element={<GuestGate><PortalLogin /></GuestGate>} />
          <Route path="/portal/register" element={<GuestGate><PortalRegister /></GuestGate>} />
          <Route path="/partner/apply" element={<GuestGate><ContractorApply /></GuestGate>} />

          {/* Contractor marketplace portal */}
          <Route element={<ContractorGuard />}>
            <Route path="/partner" element={<ContractorPortal />} />
          </Route>

          {/* Staff / office console */}
          <Route element={<StaffGuard />}>
            {/* Focused, full-screen staff surfaces (no inspector sidebar) */}
            <Route path="/mode" element={<ModeSelect />} />
            <Route path="/engineer" element={<EngineerJobs />} />
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/buildings" element={<Buildings />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/buildings/:id" element={<BuildingDetail />} />
              <Route path="/buildings/:id/plans" element={<FloorPlans />} />
              <Route path="/doors/:id" element={<DoorDetail />} />
              <Route path="/inspections" element={<Inspections />} />
              <Route path="/inspections/new" element={<InspectionForm />} />
              <Route path="/inspections/:id" element={<InspectionDetail />} />
              <Route path="/inspections/:id/edit" element={<InspectionForm />} />
              <Route path="/quotes" element={<Quotes />} />
              <Route path="/quotes/new" element={<QuoteCreate />} />
              <Route path="/quotes/:id" element={<QuoteDetail />} />
              <Route path="/quotes/:id/full-edit" element={<QuoteFullEdit />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/invoices/new" element={<InvoiceCreate />} />
              <Route path="/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/inspection-requests" element={<InspectionRequests />} />
              <Route path="/reinspections" element={<Reinspections />} />
              <Route path="/buildings/:id/remedial" element={<RemedialSchedule />} />
              <Route path="/buildings/:id/labels" element={<LabelSheet mode="building" />} />
              <Route path="/doors/:id/label" element={<LabelSheet mode="door" />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/maintenance/:id" element={<MaintenanceDetail />} />
              <Route path="/schedule" element={<Schedule />} />
              <Route path="/imports" element={<ExternalReports />} />
              <Route path="/imports/:id" element={<ExternalReportDetail />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/reports/build" element={<ReportBuilder />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/platform" element={<Platform />} />
            </Route>
          </Route>

          {/* Client portal */}
          <Route element={<ClientGuard />}>
            <Route element={<PortalLayout />}>
              <Route path="/portal" element={<PortalHome />} />
              <Route path="/portal/quotes" element={<PortalQuotes />} />
              <Route path="/portal/quotes/:id" element={<PortalQuoteDetail />} />
              <Route path="/portal/tenders" element={<PortalTenders />} />
              <Route path="/portal/invoices" element={<PortalInvoices />} />
              <Route path="/portal/invoices/:id" element={<PortalInvoiceDetail />} />
              <Route path="/portal/buildings" element={<PortalBuildings />} />
              <Route path="/portal/buildings/:id" element={<PortalBuildingDetail />} />
              <Route path="/portal/request-inspection" element={<PortalRequestInspection />} />
              <Route path="/portal/report-failure" element={<PortalReportFailure />} />
              <Route path="/portal/upload-report" element={<PortalUploadReport />} />
              <Route path="/portal/maintenance" element={<PortalMaintenance />} />
              <Route path="/portal/account" element={<PortalAccount />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
